import { useState } from 'react';
import Button, { ButtonWidth } from '../Button';
import EyeSlashIcon from 'icons/EyeSlashIcon';
import useToggleable from 'hooks/useToggleable';
import TextInput from '../inputs/TextInput';
import SelectInput from '../inputs/SelectInput';
import styles from './styles/Form.module.scss';
import RegisterHeader from './RegisterHeader';
import AngleBracket from 'icons/AngleBracket';
import USER_TYPES, {
  FAN,
  COACH,
  ATHLETIC_DIRECTOR,
  FAN_DESCRIPTION,
  ATHLETIC_DIRECTOR_DESCRIPTION,
  COACH_DESCRIPTION,
  UserTypeType
} from 'constants/UserTypes';
import { registerUser } from 'stores/userStore';
import { isEmailValid, isUsernameValid } from 'helpers/user';
import Spinner from '../Spinner';
import { getAuthModalTypeFromPath } from './AuthModalV2';
import { useApplicationContainer } from 'containers/ApplicationContainer';
import useThrottledEffect from 'hooks/useThrottledEffect';
import queries from 'queries';

const userTypeDescription = (userType: UserTypeType) => {
  switch (userType.id) {
    case COACH:
      return COACH_DESCRIPTION;
    case ATHLETIC_DIRECTOR:
      return ATHLETIC_DIRECTOR_DESCRIPTION;
    case FAN:
      return FAN_DESCRIPTION;
  }
};

export default function RegisterForm({ login }: { login: () => void }) {
  const [{ path }, { handleCloseAuthModal, handleOpenLink }] = useApplicationContainer();
  const [email, setEmail] = useState<string | undefined>('');
  const [username, setUsername] = useState<string | undefined>('');
  const [password, setPassword] = useState<string | undefined>('');
  const [passwordConfirmation, setPasswordConfirmation] = useState<string | undefined>('');
  const [userType, setUserType] = useState(USER_TYPES[0]);
  const [registrationStep, setRegistrationStep] = useState<number>(1);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const registerRoute = getAuthModalTypeFromPath(path);

  const [clientSideEmailError, setClientSideEmailError] = useState<boolean>(false);
  const [clientSideUsernameError, setClientSideUsernameError] = useState<boolean>(false);
  const [clientSidePasswordError, setClientSidePasswordError] = useState<boolean>(false);
  const [registrationErrors, setRegistrationErrors] = useState<string[] | null>(null);

  const [emailTaken, setEmailTaken] = useState(false);
  useThrottledEffect(() => {
    if (email && email.length > 3 && isEmailValid(email)) {
      queries.registerUserQuery({ email }).then(({ user }) => {
        setEmailTaken(user);
      });
    }
  }, [email]);

  const [usernameTaken, setUsernameTaken] = useState(false);
  useThrottledEffect(() => {
    if (username && username.length > 3 && isUsernameValid(username)) {
      queries.registerUserQuery({ username }).then(({ user }) => setUsernameTaken(user));
    }
  }, [username]);

  const [showPassword, { handleToggle: handleToggleShowPassword }] = useToggleable(false);

  const moveToStepTwo = () => {
    if (!email || !isEmailValid(email)) {
      setClientSideEmailError(true);
    } else {
      setClientSideEmailError(false);
      setRegistrationStep(2);
    }
  };

  const moveToStepThree = () => {
    if (!username || !isUsernameValid(username)) {
      setClientSideUsernameError(true);
    } else if (!password || password.length < 8 || password !== passwordConfirmation) {
      setClientSidePasswordError(true);
    } else {
      setClientSideUsernameError(false);
      setClientSidePasswordError(false);
      setRegistrationStep(3);
    }
  };

  function handleRegister() {
    if (!userType || !email || !username || !password || !passwordConfirmation || password !== passwordConfirmation) {
      return;
    }

    const registerNewUser = async () => {
      setIsSaving(true);
      try {
        await registerUser({
          email: email,
          username: username,
          password: password,
          password_confirmation: passwordConfirmation,
          user_type_id: userType.id
        });
        // previous register form directed FAN type users to /schools
        registerRoute ? handleOpenLink(userType.id === FAN ? '/schools' : '/') : handleCloseAuthModal();
      } catch (errors: any) {
        setRegistrationErrors(errors);
      } finally {
        setIsSaving(false);
      }
    };
    registerNewUser();
  }

  return (
    <>
      {registrationStep === 1 && (
        <>
          <RegisterHeader />
          <div className={styles.Form}>
            <div className={styles.Container}>
              <label htmlFor="email">Email</label>
              <TextInput
                onChange={val => setEmail(val)}
                value={email}
                hasError={clientSideEmailError || emailTaken}
                placeholder="Email address"
              />
              {clientSideEmailError && <div className={styles.Error}>Please enter a valid email address.</div>}
              {emailTaken && <div className={styles.Error}>Email is already taken, please use another or sign in.</div>}
            </div>
            <Button width={ButtonWidth.FULL} className={styles.RegisterButton} disabled={emailTaken} onClick={moveToStepTwo}>
              Continue
            </Button>
            <div className={styles.Secondary}>
              Already have an SBLive Sports account?&nbsp;<span onClick={login}>Sign In</span>
            </div>
          </div>
        </>
      )}
      {registrationStep === 2 && (
        <>
          <div className={styles.RegisterEmail}>
            <div onClick={() => setRegistrationStep(1)} className={styles.BackArrow}>
              <AngleBracket />
            </div>
            <div className={styles.HeaderContainer}>
              <div className={styles.Header}>Pick a username and password</div>
              <div className={styles.SubHead}>Choose a unique username and set a secure password</div>
            </div>
          </div>
          <div className={styles.Form}>
            <div className={styles.Container}>
              <label htmlFor="username">Username</label>
              <TextInput
                onChange={val => setUsername(val)}
                value={username}
                hasError={clientSideUsernameError || usernameTaken}
                placeholder="Username"
              />
              {clientSideUsernameError && <div className={styles.Error}>Username can only contian letter and numbers.</div>}
              {usernameTaken && <div className={styles.Error}>Username already taken, please try another.</div>}
            </div>
            <div className={`${styles.Container} ${styles.PasswordContainer}`}>
              <label htmlFor="password">Password</label>
              <TextInput
                onChange={val => setPassword(val)}
                value={password}
                hasError={clientSidePasswordError}
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
              />
              <div className={styles.PasswordToggle} onClick={handleToggleShowPassword}>
                <EyeSlashIcon />
              </div>
              {clientSidePasswordError && <div className={styles.Error}>Passwords must match and be at least eight characters.</div>}
            </div>
            <div className={`${styles.Container} ${styles.PasswordContainer}`}>
              <label htmlFor="passwordConfirmation">Confirm Password</label>
              <TextInput
                onChange={val => setPasswordConfirmation(val)}
                value={passwordConfirmation}
                hasError={clientSidePasswordError}
                type={showPassword ? 'text' : 'password'}
                placeholder="Confirm password"
              />
            </div>
            <Button width={ButtonWidth.FULL} className={styles.RegisterButton} disabled={usernameTaken} onClick={moveToStepThree}>
              Continue
            </Button>
          </div>
        </>
      )}
      {registrationStep === 3 && (
        <>
          <div className={styles.RegisterEmail}>
            <div onClick={() => setRegistrationStep(2)} className={styles.BackArrow}>
              <AngleBracket />
            </div>
            <div className={styles.HeaderContainer}>
              <div className={styles.Header}>What best describes you?</div>
              <div className={styles.SubHead}>Select the user type that best describes you</div>
            </div>
          </div>
          <div className={styles.Form}>
            <div className={styles.Container}>
              <label htmlFor="usertype">User Type</label>
              <SelectInput
                options={USER_TYPES}
                value={userType}
                valueExtractor={v => USER_TYPES.find(t => t === v)?.name}
                labelExtractor={v => v.name}
                onChange={v => v && setUserType(v)}
              />
            </div>
            <div className={styles.SubHead}>{userTypeDescription(userType)}</div>
            <Button width={ButtonWidth.FULL} className={styles.RegisterButton} onClick={handleRegister}>
              {isSaving ? <Spinner /> : 'Create Your SBLive Account'}
            </Button>
          </div>
        </>
      )}
      {registrationErrors?.length && (
        <>
          {registrationErrors.map((error, index) => (
            <div className={styles.Error} key={`registration-error-${index}`}>
              {error}
            </div>
          ))}
        </>
      )}
    </>
  );
}
